import React, { useEffect, useState } from "react";
import { Table, Avatar, Tag, Input, Modal, message, Button } from "antd";
import {
  UserOutlined,
  SearchOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Container } from "./style";
import { formatDateByLocale } from "../../../utils/dateFormat";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import SubscribersModal from "./SubscribersModal";
const { confirm } = Modal;

const LIST_COACHES = gql`
  query ListCoaches($filters: FilterCoachesInput, $args: PaginationArgs) {
    listCoaches(filters: $filters, args: $args) {
      count
      limit
      offset
      total
      coaches {
        _id
        name
        email
        profile_image
        subscriberCount
        createdAt
        updatedAt
        lastLoginAt
      }
    }
  }
`;

const DELETE_COACH_ACCOUNT = gql`
  mutation DeleteCoachAccount($email: String!) {
    deleteCoachAccount(email: $email) {
      success
    }
  }
`;

export default function CoachManager() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [subscribersModalVisible, setSubscribersModalVisible] = useState(false);
  const [coachId, setCoachId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [getCoaches, { data: coachesData, refetch }] =
    useLazyQuery(LIST_COACHES);
  const [deleteCoachAccount] = useMutation(DELETE_COACH_ACCOUNT);
  const [tableParams, setTableParams] = useState({
    filters: {},
    args: {
      limit: 10,
      offset: 0,
    },
  });

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableParams)]);

  useEffect(() => {
    if (coachesData?.listCoaches?.coaches) {
      setList(coachesData.listCoaches.coaches || []);
      setTotal(coachesData.listCoaches.total || 0);
    } else {
      setList([]);
      setTotal(0);
    }
  }, [coachesData]);

  const coachColumns = [
    {
      title: "Coach Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Avatar
            src={record.profile_image}
            icon={!record.profile_image ? <UserOutlined /> : null}
            style={{
              backgroundColor: !record.profile_image ? "#547756" : undefined,
            }}
          />
          <div style={{ fontWeight: 500, fontSize: "14px" }}>{name}</div>
        </div>
      ),
    },
    {
      title: "Coach Email",
      dataIndex: "email",
      key: "email",
      render: (email) => email || "-",
    },
    {
      title: "Joined Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDateByLocale(date),
    },
    {
      title: "Last Active",
      dataIndex: "lastLoginAt",
      key: "lastActive",
      render: (date) => formatDateByLocale(date),
    },
    {
      title: "Total Subscribers",
      dataIndex: "subscriberCount",
      key: "subscriberCount",
      render: (count, record) => (
        <Tag
          color="blue"
          onClick={() => handleShowSubscribersModal(record._id)}
          style={{ cursor: "pointer" }}
        >
          {count || 0}
        </Tag>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      width: 80,
      render: (action, record) => (
        <Button
          type="text"
          block
          className="text-left"
          onClick={() => onDelete(record)}
        >
          <DeleteOutlined />
        </Button>
      ),
    },
  ];

  const onSearch = (val) => {
    const searchValue = val.target.value.trim();
    setTableParams({
      filters: searchValue ? { email: searchValue } : {},
      args: tableParams.args,
    });
  };

  const handleShowSubscribersModal = (coachId) => {
    setCoachId(coachId);
    setSubscribersModalVisible(true);
  };

  const handleCloseSubscribersModal = () => {
    setSubscribersModalVisible(false);
    setCoachId(null);
  };

  const getData = async () => {
    if (!tableParams.args || !tableParams.args.limit) {
      return;
    }
    setLoading(true);
    await refetch(tableParams);
    setLoading(false);
  };

  const onTableChange = (pagination, filters, sorter) => {
    console.log(pagination);
    setTableParams({
      filters: tableParams.filters,
      args: {
        limit: pagination.pageSize,
        offset: pagination.pageSize * (pagination.current - 1),
      },
    });
  };

  const onDelete = (data) => {
    confirm({
      title: `Delete the coach "${data.name}"?`,
      content:
        "Deleting this coach will permanently remove it from system. Are you sure to delete this coach?",
      okText: "Yes, delete this coach",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        setLoading(true);
        await deleteCoachAccount({
          variables: {
            email: data.email,
          },
        });
        message.success(`Coach "${data.name}" is deleted successfully!`);
        getData();
        setLoading(false);
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  return (
    <Container>
      <div className="flex-between">
        <div className="page-title">Coach Manager</div>
      </div>
      <div className="flex-between">
        <Input
          size="large"
          placeholder="Search by email"
          bordered={false}
          prefix={
            <SearchOutlined style={{ fontSize: "20px", marginRight: "10px" }} />
          }
          className="table-search"
          onPressEnter={onSearch}
        />
      </div>
      <div className="content-tabs">
        <Table
          columns={coachColumns}
          rowKey={(record) => record._id}
          dataSource={list}
          pagination={{ total }}
          loading={loading}
          onChange={onTableChange}
        />
      </div>
      <SubscribersModal
        open={subscribersModalVisible}
        coachId={coachId}
        onCancel={handleCloseSubscribersModal}
      />
    </Container>
  );
}
